/* eslint react/jsx-props-no-spreading: 0 */
import {
    Box,
    Stack,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    Typography,
    FormHelperText,
} from '@mui/material';
import { Accordion, Checkbox, FormStepComponentProps, PasswordField, SortedSelect, TextField } from 'common';
import { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { buyerSignUpFormText } from '../BuyerSignUp.constants';
import { BuyerSignupFormSchema } from './../BuyerSignUp.schema';
import {
    DeliveryMethodOutbound,
    FileType,
    SftpProtocol,
    sftpProtocolOptions,
    webserviceOptions,
} from '../../../constants';

const text = buyerSignUpFormText.outboundDataFeeds;

type OutboundDataFeedsProps = FormStepComponentProps<typeof BuyerSignupFormSchema>;

export const OutboundDataFeeds = ({ control }: OutboundDataFeedsProps) => {
    // react-hook-form hooks
    const { setValue, getValues, clearErrors } = useFormContext(); // component wrapped with FormProvider in MultiStepForm.tsx

    function getSelectedDeliveryMethod(): DeliveryMethodOutbound {
        const deliveryMethods = [
            DeliveryMethodOutbound.SFTP,
            DeliveryMethodOutbound.Email,
            DeliveryMethodOutbound.Oracle,
            DeliveryMethodOutbound.Transcepta,
            DeliveryMethodOutbound.Webservice,
        ];
        for (const method of deliveryMethods) {
            if (getValues(`outboundDataFeeds.deliveryMethod.use${method}`)) {
                return method;
            }
        }
        return DeliveryMethodOutbound.SFTP;
    }

    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<DeliveryMethodOutbound>(
        getSelectedDeliveryMethod()
    );

    const [useOtherFileType, setUseOtherFileType] = useState<boolean>(
        getValues('outboundDataFeeds.fileTypes.useOther') ?? false
    );

    function clearDeliveryMethodData() {
        setValue(
            'outboundDataFeeds.deliveryMethod.sftpConnection',
            {
                server: '',
                user: '',
                password: '',
                downloadFolder: '',
                uploadFolder: '',
                protocol: SftpProtocol.SFTP,
                port: '',
                timeoutInSeconds: '',
            },
            { shouldDirty: false, shouldValidate: false }
        );
        setValue(
            'outboundDataFeeds.deliveryMethod.sftpEmail',
            {
                emailAddress: '',
            },
            { shouldDirty: false, shouldValidate: false }
        );
        setValue('outboundDataFeeds.deliveryMethod.uri', '', { shouldDirty: false, shouldValidate: false });
        setValue('outboundDataFeeds.deliveryMethod.webserviceName', '', { shouldDirty: false, shouldValidate: false });
        setValue('outboundDataFeeds.deliveryMethod.useSftp', false);
        setValue('outboundDataFeeds.deliveryMethod.useEmail', false);
        setValue('outboundDataFeeds.deliveryMethod.useOracleSupplierNetwork', false);
        setValue('outboundDataFeeds.deliveryMethod.useTransceptaManager', false);
        setValue('outboundDataFeeds.deliveryMethod.useWebservice', false);
        setValue('outboundDataFeeds.deliveryMethod.useElectronic', false);
        clearErrors('outboundDataFeeds.deliveryMethod');
    }

    function handleSelectDeliveryMethod(val: DeliveryMethodOutbound) {
        clearDeliveryMethodData();
        setValue(`outboundDataFeeds.deliveryMethod.use${val}`, true);
        setSelectedDeliveryMethod(val);
    }

    function handleCheckFileType(fileType: FileType, checked: boolean) {
        clearErrors('outboundDataFeeds.fileTypes');
        setValue(`outboundDataFeeds.fileTypes.use${fileType}`, checked);

        if (fileType === FileType.Other) {
            setUseOtherFileType(checked);
            setValue('outboundDataFeeds.fileTypes.fileTypeExtension', '', {
                shouldDirty: false,
                shouldValidate: false,
            });
        }
    }

    const deliveryMethodSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.deliveryMethod.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <FormControl fullWidth>
                            <RadioGroup
                                value={selectedDeliveryMethod}
                                onChange={(e) => {
                                    handleSelectDeliveryMethod(e.target.value as DeliveryMethodOutbound);
                                }}
                            >
                                <FormControlLabel
                                    value={DeliveryMethodOutbound.SFTP}
                                    control={<Radio data-testid="select-deliveryMethod-radio-sftp" />}
                                    label="SFTP"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodOutbound.Email}
                                    control={<Radio data-testid="select-deliveryMethod-radio-email" />}
                                    label="Email"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodOutbound.Oracle}
                                    control={<Radio data-testid="select-deliveryMethod-radio-oracleSupplierNetwork" />}
                                    label="OracleSupplierNetwork"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodOutbound.Transcepta}
                                    control={<Radio data-testid="select-deliveryMethod-radio-transcepta" />}
                                    label="TransceptaManager"
                                />
                                <FormControlLabel
                                    value={DeliveryMethodOutbound.Webservice}
                                    control={<Radio data-testid="select-deliveryMethod-radio-webservice" />}
                                    label="Webservice"
                                />
                            </RadioGroup>
                        </FormControl>

                        <Stack
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                            spacing={2}
                            width="100%"
                        >
                            {selectedDeliveryMethod === DeliveryMethodOutbound.SFTP && (
                                <>
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.server"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Server"
                                                testId="enter-deliveryMethod-sftpConnection-server"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.user"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="User"
                                                testId="enter-deliveryMethod-sftpConnection-user"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.password"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <PasswordField
                                                    label="Password"
                                                    testId="enter-deliveryMethod-sftpConnection-password"
                                                    {...field}
                                                    error={fieldState.invalid}
                                                    helperText={fieldState.error?.message}
                                                    required
                                                />
                                            </>
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.downloadFolder"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Download folder"
                                                testId="enter-deliveryMethod-sftpConnection-downloadFolder"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.uploadFolder"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Upload folder"
                                                testId="enter-deliveryMethod-sftpConnection-uploadFolder"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.protocol"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <SortedSelect
                                                id="Protocol"
                                                label="Protocol"
                                                testId="enter-deliveryMethod-sftpConnection-protocol"
                                                options={sftpProtocolOptions}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                value={field.value ?? null}
                                                onChange={field.onChange}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.port"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                label="Port"
                                                testId="enter-deliveryMethod-sftpConnection-port"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="outboundDataFeeds.deliveryMethod.sftpConnection.timeoutInSeconds"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            //@ts-ignore
                                            <TextField
                                                label="Timeout (in seconds)"
                                                testId="enter-deliveryMethod-sftpConnection-timeoutInSeconds"
                                                {...field}
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message}
                                                required
                                                numericOnly
                                            />
                                        )}
                                    />
                                </>
                            )}
                            {selectedDeliveryMethod === DeliveryMethodOutbound.Email && (
                                <Controller
                                    name="outboundDataFeeds.deliveryMethod.emailAddress"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            label="Email from where data feed files will be delivered"
                                            testId="enter-deliveryMethod-emailAddress"
                                            {...field}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            required
                                        />
                                    )}
                                />
                            )}
                            {selectedDeliveryMethod === DeliveryMethodOutbound.Oracle && (
                                <Controller
                                    name="outboundDataFeeds.deliveryMethod.uri"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            label="URI"
                                            testId="enter-deliveryMethod-uri"
                                            {...field}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            required
                                        />
                                    )}
                                />
                            )}
                            {selectedDeliveryMethod === DeliveryMethodOutbound.Webservice && (
                                <Controller
                                    name="outboundDataFeeds.deliveryMethod.webserviceName"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <SortedSelect
                                            id="Webservice"
                                            label="Webservice"
                                            testId="enter-deliveryMethod-select-webservice"
                                            options={webserviceOptions}
                                            error={fieldState.invalid}
                                            helperText={fieldState.error?.message}
                                            value={field.value ?? null}
                                            onChange={field.onChange}
                                            required
                                        />
                                    )}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );

    const fileTypeSection = (
        <Box width="100%">
            <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                <Accordion
                    title={text.sections.fileType.header}
                    variant="transparent"
                    defaultExpanded
                    typographyProps={{
                        variant: 'subtitle1',
                    }}
                    sx={{ width: '100%' }}
                >
                    <Stack
                        direction="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        spacing={2}
                        width="100%"
                    >
                        <Controller
                            name="outboundDataFeeds.fileTypes.usePDF"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    label="PDF"
                                    testId="select-fileTypes-check-pdf"
                                    {...field}
                                    checked={!!field.value}
                                    value={field?.value?.toString()}
                                    onChange={(e) => handleCheckFileType(FileType.PDF, e.target.checked)}
                                />
                            )}
                        />
                        <Controller
                            name="outboundDataFeeds.fileTypes.useTIFF"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    label="TIFF"
                                    testId="select-fileTypes-check-tiff"
                                    {...field}
                                    checked={!!field.value}
                                    value={field?.value?.toString()}
                                    onChange={(e) => handleCheckFileType(FileType.TIFF, e.target.checked)}
                                />
                            )}
                        />
                        <Controller
                            name="outboundDataFeeds.fileTypes.useXML"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    label="XML"
                                    testId="select-fileTypes-check-xml"
                                    {...field}
                                    checked={!!field.value}
                                    value={field?.value?.toString()}
                                    onChange={(e) => handleCheckFileType(FileType.XML, e.target.checked)}
                                />
                            )}
                        />
                        <Controller
                            name="outboundDataFeeds.fileTypes.useXML1"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    label="XML1"
                                    testId="select-fileTypes-check-xml1"
                                    {...field}
                                    checked={!!field.value}
                                    value={field?.value?.toString()}
                                    onChange={(e) => handleCheckFileType(FileType.XML1, e.target.checked)}
                                />
                            )}
                        />
                        <Controller
                            name="outboundDataFeeds.fileTypes.useOther"
                            control={control}
                            render={({ field }) => (
                                <Stack direction="row" justifyContent="flex-start" alignItems="center" width="100%">
                                    <Checkbox
                                        label="Other"
                                        testId="select-fileTypes-check-other"
                                        {...field}
                                        checked={!!field.value}
                                        value={field?.value?.toString()}
                                        onChange={(e) => handleCheckFileType(FileType.Other, e.target.checked)}
                                    />

                                    {useOtherFileType && (
                                        <Controller
                                            name="outboundDataFeeds.fileTypes.fileTypeExtension"
                                            control={control}
                                            render={({ field: subField, fieldState: subFieldState }) => (
                                                <TextField
                                                    label="File extension"
                                                    testId="select-fileTypes-enter-extension"
                                                    {...subField}
                                                    error={subFieldState.invalid}
                                                    helperText={subFieldState.error?.message}
                                                    required
                                                    labelBoxSx={{ width: '90%' }}
                                                />
                                            )}
                                        />
                                    )}
                                </Stack>
                            )}
                        />
                        <Controller
                            name="outboundDataFeeds.fileTypes"
                            control={control}
                            render={({ fieldState }) => (
                                <FormHelperText error={!!fieldState.error}>{fieldState.error?.message}</FormHelperText>
                            )}
                        />
                    </Stack>
                </Accordion>
            </Stack>
        </Box>
    );
    return (
        <>
            <Box className="OutboundDataFeeds" width="100%">
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={4}
                    divider={<Divider orientation="horizontal" flexItem />}
                >
                    <Typography variant="h1" padding={2}>
                        {text.topHeader}
                    </Typography>
                    {deliveryMethodSection}
                    {fileTypeSection}
                </Stack>
            </Box>
        </>
    );
};
